import { dispatchCartFetch } from '@theme/seal-dispatch-snapshot';
import { CartAddEvent } from '@theme/events';

const VARIANT_BUTTON_SELECTOR = '[data-seal-cart-upsell-variant]';
const ADD_BUTTON_SELECTOR = '[data-seal-cart-upsell-add]';
const BOX_PICKER_TOGGLE_SELECTOR = '[data-seal-cart-box-picker-toggle]';
const TAP_MOVEMENT_TOLERANCE = 10;

class SealCartUpsell extends HTMLElement {
  tapPointer = null;

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointerup', this.handlePointerUp);
    this.addEventListener('pointercancel', this.handlePointerCancel);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointerup', this.handlePointerUp);
    this.removeEventListener('pointercancel', this.handlePointerCancel);
  }

  handleClick = (event) => {
    const boxPickerToggle = this.closestFromEvent(event, BOX_PICKER_TOGGLE_SELECTOR);
    if (boxPickerToggle) {
      this.handleBoxPickerToggle(event, boxPickerToggle);
      return;
    }

    const variantButton = this.closestFromEvent(event, VARIANT_BUTTON_SELECTOR);
    if (variantButton) {
      this.handleVariantSelect(event, variantButton);
      return;
    }

    const button = this.closestFromEvent(event, ADD_BUTTON_SELECTOR);
    if (!button || button.disabled) return;

    this.handleAddButton(event, button);
  };

  handlePointerDown = (event) => {
    if (event.button > 0) return;

    const variantButton = this.closestFromEvent(event, VARIANT_BUTTON_SELECTOR);
    const boxPickerToggle = variantButton ? null : this.closestFromEvent(event, BOX_PICKER_TOGGLE_SELECTOR);
    const addButton = variantButton || boxPickerToggle ? null : this.closestFromEvent(event, ADD_BUTTON_SELECTOR);
    const button = variantButton || boxPickerToggle || addButton;

    if (!button || button.disabled) return;

    this.tapPointer = {
      pointerId: event.pointerId,
      button,
      selector: variantButton ? VARIANT_BUTTON_SELECTOR : boxPickerToggle ? BOX_PICKER_TOGGLE_SELECTOR : ADD_BUTTON_SELECTOR,
      x: event.clientX,
      y: event.clientY,
    };
  };

  handlePointerUp = (event) => {
    const pointer = this.tapPointer;
    if (!pointer || pointer.pointerId !== event.pointerId) return;

    this.tapPointer = null;

    const button = this.closestFromEvent(event, pointer.selector);
    const movedX = Math.abs(event.clientX - pointer.x);
    const movedY = Math.abs(event.clientY - pointer.y);

    if (
      !button ||
      button !== pointer.button ||
      button.disabled ||
      movedX > TAP_MOVEMENT_TOLERANCE ||
      movedY > TAP_MOVEMENT_TOLERANCE
    ) {
      return;
    }

    if (pointer.selector === VARIANT_BUTTON_SELECTOR) {
      this.handleVariantSelect(event, button);
      return;
    }

    if (pointer.selector === BOX_PICKER_TOGGLE_SELECTOR) {
      this.handleBoxPickerToggle(event, button);
      return;
    }

    this.handleAddButton(event, button);
  };

  handlePointerCancel = () => {
    this.tapPointer = null;
  };

  async handleAddButton(event, button) {
    if (button.disabled) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.setButtonState(button, 'loading');

    try {
      const response = await dispatchCartFetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: this.getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cart add failed');
      }

      this.setButtonState(button, 'added');

      window.setTimeout(() => {
        this.dispatchEvent(
          new CartAddEvent(data, `seal-cart-upsell-${variantId}`, {
            source: 'seal-cart-upsell',
            itemCount: 1,
            variantId,
            sections: data.sections,
          })
        );
      }, 180);
    } catch (error) {
      console.error(error);
      this.setButtonState(button, 'error');

      window.setTimeout(() => {
        this.setButtonState(button, 'idle');
      }, 1800);
    }
  }

  handleVariantSelect(event, variantButton) {
    if (variantButton.disabled) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    const product = variantButton.closest('[data-seal-cart-upsell-product]');
    if (!product) return;

    const variantId = variantButton.dataset.variantId;
    const variantPrice = variantButton.dataset.variantPrice;
    const action = product.querySelector('[data-seal-cart-upsell-add]');
    const price = product.querySelector('[data-seal-cart-upsell-price]');
    const image = product.querySelector('[data-seal-cart-upsell-image]');

    product.querySelectorAll('[data-seal-cart-upsell-variant]').forEach((button) => {
      const isSelected = button === variantButton;
      button.classList.toggle('is-active', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (action && variantId) {
      action.dataset.variantId = variantId;
      this.setButtonState(action, 'idle');
    }

    if (price && variantPrice) {
      price.textContent = variantPrice;
    }

    this.updateVariantImage(image, variantButton);
  }

  handleBoxPickerToggle(event, toggleButton) {
    if (toggleButton.disabled) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    const targetView = toggleButton.dataset.sealCartBoxPickerToggle;
    const picker = toggleButton.closest('[data-seal-cart-box-picker]');
    if (!targetView || !picker) return;

    let visibleView = null;

    picker.querySelectorAll('[data-seal-cart-box-picker-view]').forEach((view) => {
      const isTarget = view.dataset.sealCartBoxPickerView === targetView;
      view.hidden = !isTarget;
      view.classList.toggle('is-active', isTarget);

      if (isTarget) {
        visibleView = view;
      }
    });

    const selectedVariant = visibleView?.querySelector(`${VARIANT_BUTTON_SELECTOR}.is-active:not(:disabled)`);
    const fallbackVariant = visibleView?.querySelector(`${VARIANT_BUTTON_SELECTOR}:not(:disabled)`);

    if (!selectedVariant && fallbackVariant) {
      this.handleVariantSelect(event, fallbackVariant);
    }
  }

  updateVariantImage(image, variantButton) {
    if (!(image instanceof HTMLImageElement)) return;

    const { variantImageSrc, variantImageSrcset, variantImageSizes, variantImageAlt } = variantButton.dataset;
    if (!variantImageSrc) return;

    if (image.dataset.sealCartUpsellImageSrc === variantImageSrc && image.complete) {
      this.setImageLoading(image, false);
      return;
    }

    const requestId = `${Date.now()}-${Math.random()}`;
    const preloadImage = new Image();

    image.dataset.sealCartUpsellImageRequest = requestId;
    this.setImageLoading(image, true);

    const finish = () => {
      if (image.dataset.sealCartUpsellImageRequest !== requestId) return;

      if (variantImageSizes) {
        image.sizes = variantImageSizes;
      }

      if (variantImageSrcset) {
        image.srcset = variantImageSrcset;
      }

      image.src = variantImageSrc;

      if (variantImageAlt !== undefined) {
        image.alt = variantImageAlt;
      }

      image.dataset.sealCartUpsellImageSrc = variantImageSrc;
      this.setImageLoading(image, false);
    };

    const fail = () => {
      if (image.dataset.sealCartUpsellImageRequest !== requestId) return;

      this.setImageLoading(image, false);
    };

    if (variantImageSrcset) {
      preloadImage.srcset = variantImageSrcset;
    }

    if (variantImageSizes) {
      preloadImage.sizes = variantImageSizes;
    }

    preloadImage.onload = finish;
    preloadImage.onerror = fail;
    preloadImage.src = variantImageSrc;

    if (preloadImage.complete && preloadImage.naturalWidth > 0) {
      finish();
    }
  }

  setImageLoading(image, isLoading) {
    const product = image.closest('[data-seal-cart-upsell-product]');
    product?.classList.toggle('is-image-loading', isLoading);
  }

  closestFromEvent(event, selector) {
    const path = event.composedPath?.() || [];
    const pathMatch = path.find((target) => target instanceof Element && target.matches(selector));

    if (pathMatch && this.contains(pathMatch)) return pathMatch;

    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const match = target?.closest?.(selector);

    return match && this.contains(match) ? match : null;
  }

  getCartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  setButtonState(button, state) {
    const liveLabel = button.querySelector('.visually-hidden');
    const defaultLabel = button.dataset.label || '';
    const addedLabel = button.dataset.addedLabel || defaultLabel;
    const errorLabel = button.dataset.errorLabel || defaultLabel;

    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-added', state === 'added');
    button.classList.toggle('is-error', state === 'error');
    button.disabled = state === 'loading' || state === 'added';

    if (!liveLabel) return;

    if (state === 'added') {
      liveLabel.textContent = addedLabel;
    } else if (state === 'error') {
      liveLabel.textContent = errorLabel;
    } else {
      liveLabel.textContent = defaultLabel;
    }
  }
}

if (!customElements.get('seal-cart-upsell')) {
  customElements.define('seal-cart-upsell', SealCartUpsell);
}
